import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Tabs, Space, Card, message, Typography, Tooltip,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import { useRolePreview } from '../../contexts/RolePreviewContext';
import {
  listControlTypes, createControlType, updateControlType, deleteControlType,
  listLicenseGroupTypes, createLicenseGroupType, updateLicenseGroupType, deleteLicenseGroupType,
  listPackages, createPackage, updatePackage, deletePackage,
  listGroupTypes, createGroupType, updateGroupType, deleteGroupType,
  listIwmProfiles,
  seedDefinitions,
  ControlType, LicenseGroupType, WorkspacePackage, GroupType, IwmProfile,
} from '../../services/workspaceApi';

function labelById(
  rows: { id: number; code: string; displayName: string }[],
  id: number | null | undefined,
): string {
  if (id == null) return '—';
  const row = rows.find((r) => r.id === id);
  return row ? `${row.code} — ${row.displayName}` : `#${id}`;
}

function formatJsonMap(raw: string | undefined | null): string {
  if (!raw || raw === '{}' || raw === 'null') return '—';
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    const entries = Object.entries(obj);
    if (entries.length === 0) return '—';
    return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
  } catch {
    return raw;
  }
}

type DefRow = ControlType | LicenseGroupType | WorkspacePackage | GroupType;

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

type CatalogRow = { id: number; code: string; displayName: string };

const catalogLabel = (row: CatalogRow) => `${row.code} — ${row.displayName}`;

const resolveCatalogLabel = (rows: CatalogRow[], id: number | null | undefined) => {
  if (id == null) return null;
  const row = rows.find((r) => r.id === id);
  return row ? catalogLabel(row) : String(id);
};

const DefinitionsPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const { canWriteWorkspace: canWrite } = useRolePreview();

  const [controlTypes, setControlTypes] = useState<ControlType[]>([]);
  const [licenseGroupTypes, setLicenseGroupTypes] = useState<LicenseGroupType[]>([]);
  const [packages, setPackages] = useState<WorkspacePackage[]>([]);
  const [groupTypes, setGroupTypes] = useState<GroupType[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<string>('control');
  const [editing, setEditing] = useState<DefRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // IWM profile catalog for the Package "IWM Profile" picker — fetched lazily (only when the
  // packages tab's modal opens), since IWM may be unreachable and we don't want to block page load.
  const [iwmProfiles, setIwmProfiles] = useState<IwmProfile[]>([]);
  const [iwmProfilesLoading, setIwmProfilesLoading] = useState(false);
  const [iwmProfilesError, setIwmProfilesError] = useState<string | null>(null);
  const loadIwmProfiles = useCallback(async () => {
    setIwmProfilesLoading(true);
    setIwmProfilesError(null);
    try {
      setIwmProfiles(await listIwmProfiles());
    } catch (e) {
      setIwmProfiles([]);
      setIwmProfilesError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'IWM unreachable');
    } finally {
      setIwmProfilesLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ct, lgt, pkg, gt] = await Promise.all([
        listControlTypes(),
        listLicenseGroupTypes(),
        listPackages(),
        listGroupTypes(),
      ]);
      setControlTypes(Array.isArray(ct) ? ct : []);
      setLicenseGroupTypes(Array.isArray(lgt) ? lgt : []);
      setPackages(Array.isArray(pkg) ? pkg : []);
      setGroupTypes(Array.isArray(gt) ? gt : []);
    } catch {
      message.error('Failed to load definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdd = (tab: string) => {
    setModalTab(tab);
    setEditing(null);
    form.resetFields();
    if (tab === 'control') form.setFieldsValue({ extensionsAllowed: false });
    if (tab === 'packages') loadIwmProfiles();
    setModalOpen(true);
  };

  const openEdit = (tab: string, record: DefRow) => {
    setModalTab(tab);
    setEditing(record);
    form.setFieldsValue(record);
    if (tab === 'packages') loadIwmProfiles();
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const id = editing?.id;
      if (modalTab === 'control') {
        if (id) await updateControlType(id, values);
        else await createControlType(values);
      } else if (modalTab === 'license') {
        if (id) await updateLicenseGroupType(id, values);
        else await createLicenseGroupType(values);
      } else if (modalTab === 'packages') {
        if (id) await updatePackage(id, values);
        else await createPackage(values);
      } else {
        if (id) await updateGroupType(id, values);
        else await createGroupType(values);
      }
      message.success(id ? 'Updated' : 'Created');
      setModalOpen(false);
      form.resetFields();
      await loadAll();
    } catch (e) {
      const m = errMsg(e, 'Save failed');
      if (m) message.error(m);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (tab: string, record: DefRow) => {
    Modal.confirm({
      title: 'Delete this definition?',
      content: `Remove "${(record as { code?: string; displayName?: string }).code || (record as { displayName?: string }).displayName}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          if (tab === 'control') await deleteControlType(record.id);
          else if (tab === 'license') await deleteLicenseGroupType(record.id);
          else if (tab === 'packages') await deletePackage(record.id);
          else await deleteGroupType(record.id);
          message.success('Deleted');
          await loadAll();
        } catch (e) {
          message.error(errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedDefinitions();
      message.success('Defaults seeded');
      await loadAll();
    } catch (e) {
      message.error(errMsg(e, 'Seed failed') || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const actionCol = (tab: string) =>
    canWrite
      ? {
          title: 'Actions',
          key: 'actions',
          width: 140,
          render: (_: unknown, record: DefRow) => (
            <Space>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(tab, record)}>
                Edit
              </Button>
              <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(tab, record)}>
                Delete
              </Button>
            </Space>
          ),
        }
      : null;

  const controlCols = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 140 },
    { title: 'Name', dataIndex: 'displayName', key: 'displayName' },
    { title: 'Max concurrent', dataIndex: 'maxConcurrent', key: 'maxConcurrent', width: 120 },
    { title: 'Period (d)', dataIndex: 'periodDays', key: 'periodDays', width: 100 },
    { title: 'Max (h)', dataIndex: 'maxHours', key: 'maxHours', width: 90 },
    {
      title: 'Extensions',
      dataIndex: 'extensionsAllowed',
      key: 'extensionsAllowed',
      width: 100,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    actionCol('control'),
  ].filter(Boolean);

  const licenseCols = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 140 },
    { title: 'Name', dataIndex: 'displayName', key: 'displayName' },
    {
      title: 'Tools',
      dataIndex: 'toolsJson',
      key: 'toolsJson',
      ellipsis: true,
      render: (v: string) => {
        if (!v) return '—';
        try {
          return (JSON.parse(v) as string[]).join(', ');
        } catch {
          return v;
        }
      },
    },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    actionCol('license'),
  ].filter(Boolean);

  const packageCols = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 140 },
    { title: 'Name', dataIndex: 'displayName', key: 'displayName' },
    {
      title: 'License Group Type',
      dataIndex: 'licenseGroupTypeId',
      key: 'licenseGroupTypeId',
      width: 200,
      render: (v: number | null | undefined) => {
        const label = resolveCatalogLabel(licenseGroupTypes, v);
        if (!label) return <Typography.Text type="secondary">—</Typography.Text>;
        return label;
      },
    },
    {
      title: 'Control Type',
      dataIndex: 'controlTypeId',
      key: 'controlTypeId',
      width: 180,
      render: (v: number | null | undefined) => {
        const label = resolveCatalogLabel(controlTypes, v);
        if (!label) return <Typography.Text type="secondary">—</Typography.Text>;
        return label;
      },
    },
    {
      title: 'License Counts',
      dataIndex: 'licenseCountsJson',
      key: 'licenseCountsJson',
      ellipsis: true,
      render: (v: string) => formatJsonMap(v),
    },
    {
      title: 'IWM Profile', dataIndex: 'iwmProfileId', key: 'iwmProfileId', width: 160,
      render: (v: string | null | undefined) => v || <Typography.Text type="secondary">— not mapped —</Typography.Text>,
    },
    actionCol('packages'),
  ].filter(Boolean);

  const groupTypeCols = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 100 },
    { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 120 },
    { title: 'Name', dataIndex: 'displayName', key: 'displayName' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    actionCol('groupTypes'),
  ].filter(Boolean);

  const addBtn = (tab: string, label: string) =>
    canWrite ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openAdd(tab)}>
        {label}
      </Button>
    ) : null;

  const modalTitle = editing
    ? `Edit ${modalTab === 'control' ? 'Control Type' : modalTab === 'license' ? 'License Group Type' : modalTab === 'packages' ? 'Package' : 'Group Type'}`
    : `Add ${modalTab === 'control' ? 'Control Type' : modalTab === 'license' ? 'License Group Type' : modalTab === 'packages' ? 'Package' : 'Group Type'}`;

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="Workspace Definitions"
            purpose="Master data: control types, license group types, packages, and group types."
            actions={
              canWrite ? (
                <Button loading={seeding} onClick={handleSeed}>
                  Seed defaults
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">
              Workspace Definitions
            </Typography.Title>
            {canWrite && (
              <Button loading={seeding} onClick={handleSeed}>
                Seed defaults
              </Button>
            )}
          </div>
        )}

        <Tabs
          items={[
            {
              key: 'control',
              label: 'Control Types',
              children: (
                <>
                  <div className="mb-2 flex justify-end">{addBtn('control', 'Add Control Type')}</div>
                  <Table rowKey="id" size="small" loading={loading} columns={controlCols as any} dataSource={controlTypes} pagination={false} />
                </>
              ),
            },
            {
              key: 'license',
              label: 'License Group Types',
              children: (
                <>
                  <div className="mb-2 flex justify-end">{addBtn('license', 'Add License Group Type')}</div>
                  <Table rowKey="id" size="small" loading={loading} columns={licenseCols as any} dataSource={licenseGroupTypes} pagination={false} />
                </>
              ),
            },
            {
              key: 'packages',
              label: 'Packages',
              children: (
                <>
                  <div className="mb-2 flex justify-end">{addBtn('packages', 'Add Package')}</div>
                  <Table rowKey="id" size="small" loading={loading} columns={packageCols as any} dataSource={packages} pagination={false} />
                </>
              ),
            },
            {
              key: 'groupTypes',
              label: 'Group Types',
              children: (
                <>
                  <div className="mb-2 flex justify-end">{addBtn('groupTypes', 'Add Group Type')}</div>
                  <Table rowKey="id" size="small" loading={loading} columns={groupTypeCols as any} dataSource={groupTypes} pagination={false} />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={modalTitle}
        open={modalOpen}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={editing ? 'Save' : 'Add'}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. hours" />
          </Form.Item>
          <Form.Item name="displayName" label="Display Name" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          {modalTab === 'groupTypes' && (
            <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="batch | individual | corporate | trainer" />
            </Form.Item>
          )}
          {modalTab === 'control' && (
            <>
              <Form.Item name="maxConcurrent" label="Max Concurrent (group pool)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="periodDays" label="Period Days">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="maxHours" label="Max Hours">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="extensionsAllowed" label="Allow Extensions" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
          {modalTab === 'license' && (
            <Form.Item name="toolsJson" label="Tools JSON" extra='JSON array, e.g. ["vcs","verdi"]'>
              <Input.TextArea rows={2} />
            </Form.Item>
          )}
          {modalTab === 'packages' && (
            <>
              <Form.Item name="licenseGroupTypeId" label="License Group Type">
                <Select
                  showSearch
                  allowClear
                  placeholder="e.g. CC — Custom Compiler"
                  options={licenseGroupTypes.map((row) => ({ value: row.id, label: catalogLabel(row) }))}
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="controlTypeId" label="Control Type">
                <Select
                  showSearch
                  allowClear
                  placeholder="e.g. max4_group — Max concurrent"
                  options={controlTypes.map((row) => ({ value: row.id, label: catalogLabel(row) }))}
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="licenseCountsJson"
                label="License counts (JSON)"
                extra='Per-tool seats, e.g. {"cc":20,"hspice":20}'
              >
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="toolsJson" label="Tools JSON" extra='JSON array of tool names'>
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item
                name="iwmProfileId"
                label={
                  <span>
                    IWM Profile{' '}
                    {iwmProfilesError && (
                      <Tooltip title={`Could not load IWM's profile catalog (${iwmProfilesError}). You can still type a profile id, but it won't be validated.`}>
                        <Typography.Text type="warning" style={{ fontSize: 12 }}>(catalog unavailable)</Typography.Text>
                      </Tooltip>
                    )}
                  </span>
                }
                extra="Sessions using this package launch with this IWM profile. Picked from IWM's live catalog, not free text."
              >
                {iwmProfilesError ? (
                  <Input placeholder="IWM profile id (catalog unavailable — enter manually)" />
                ) : (
                  <Select
                    showSearch
                    allowClear
                    loading={iwmProfilesLoading}
                    placeholder="Select an IWM profile"
                    options={iwmProfiles.map((p) => ({ value: p.id, label: p.displayName ? `${p.id} — ${p.displayName}` : p.id }))}
                    filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                  />
                )}
              </Form.Item>
              <Space className="w-full" size="middle">
                <Form.Item
                  name="memoryRequest"
                  label="Memory Request"
                  className="flex-1"
                  extra="e.g. 4Gi — no CPU field in IWM's session-spec contract"
                >
                  <Input placeholder="4Gi" />
                </Form.Item>
                <Form.Item name="memoryLimit" label="Memory Limit" className="flex-1">
                  <Input placeholder="8Gi" />
                </Form.Item>
              </Space>
            </>
          )}
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DefinitionsPage;
